import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect, useRef, useMemo } from 'react';
import { ChatConversation, ChatMessage, ReportedMessage, Reaction, Timestamp } from './types';
import { useAuth } from './AuthContext';
import { db, Timestamp as TimestampValue } from './firebase';
import { sendDirectNotification } from './notificationUtils';

interface ChatContextType {
    conversations: ChatConversation[];
    messages: { [key: string]: ChatMessage[] };
    totalUnreadCount: number;
    sendMessage: (conversationId: string, message: { text?: string; imageUrl?: string | null }) => Promise<void>;
    editMessage: (conversationId: string, messageId: string, newText: string) => Promise<void>;
    deleteMessage: (conversationId: string, messageId: string) => Promise<void>;
    deleteConversation: (conversationId: string) => Promise<void>;
    reportMessage: (conversationId: string, messageId: string, senderId: string, reason: string, details: string) => Promise<void>;
    getOrCreateConversation: (recipientId: string) => Promise<string>;
    markAsRead: (conversationId: string) => void;
    addReactionToMessage: (conversationId: string, messageId: string, emoji: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { userProfile } = useAuth();
    const [conversations, setConversations] = useState<ChatConversation[]>([]);
    const [messages, setMessages] = useState<{ [key: string]: ChatMessage[] }>({});
    const activeListeners = useRef<Map<string, () => void>>(new Map());
    
    useEffect(() => {
        if (!userProfile || !db) {
            setConversations([]);
            setMessages({});
            activeListeners.current.forEach(unsubscribe => unsubscribe());
            activeListeners.current.clear();
            return;
        }

        const unsubscribeConvos = db.collection('conversations')
            .where('participants', 'array-contains', userProfile.uid)
            .onSnapshot((snapshot: any) => {
                const convosData = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as ChatConversation));
                
                // Sort client-side
                convosData.sort((a: any, b: any) => {
                    const timeA = a.lastMessage?.createdAt?.toMillis() || 0;
                    const timeB = b.lastMessage?.createdAt?.toMillis() || 0;
                    return timeB - timeA;
                });

                const currentConvoIds = new Set(convosData.map((c: any) => c.id));

                activeListeners.current.forEach((unsubscribe, convoId) => {
                    if (!currentConvoIds.has(convoId)) {
                        unsubscribe();
                        activeListeners.current.delete(convoId);
                    }
                });

                setConversations(convosData);

                convosData.forEach((convo: any) => {
                    if (!activeListeners.current.has(convo.id)) {
                        const unsubscribeMsgs = db.collection('conversations').doc(convo.id).collection('messages')
                            .orderBy('createdAt', 'asc')
                            .limit(50)
                            .onSnapshot((msgSnapshot: any) => {
                                const msgsData = msgSnapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() } as ChatMessage));
                                setMessages(prev => ({ ...prev, [convo.id]: msgsData }));
                            });
                        activeListeners.current.set(convo.id, unsubscribeMsgs);
                    }
                });
            });

        return () => {
            unsubscribeConvos();
            activeListeners.current.forEach(unsubscribe => unsubscribe());
            activeListeners.current.clear();
        };
    }, [userProfile]);
    
    const totalUnreadCount = useMemo(() => {
        if (!userProfile) return 0;
        return Object.values(messages)
            .flat()
            .filter((msg: ChatMessage) => !msg.read && msg.senderId !== userProfile.uid).length;
    }, [messages, userProfile]);

    const sendMessage = async (conversationId: string, message: { text?: string; imageUrl?: string | null }) => {
        if (!userProfile || !db) return;

        const newMessage: any = {
            senderId: userProfile.uid,
            createdAt: TimestampValue.now(),
            read: false,
        };
        if (message.text) newMessage.text = message.text;
        if (message.imageUrl) newMessage.imageUrl = message.imageUrl;
        
        try {
            await db.collection('conversations').doc(conversationId).collection('messages').add(newMessage);
            
            await db.collection('conversations').doc(conversationId).update({ 
                lastMessage: { ...newMessage, conversationId },
            });

            // Find recipient to notify
            const conversation = conversations.find(c => c.id === conversationId);
            if (conversation) {
                const recipientId = conversation.participants.find(p => p !== userProfile.uid);
                if (recipientId) {
                    sendDirectNotification(recipientId, {
                        type: 'new_message',
                        text: `sent you a message.`,
                        fromUser: {
                            id: userProfile.uid,
                            name: userProfile.displayName,
                            avatar: userProfile.photoURL,
                        }
                    });
                }
            }

        } catch (error) {
            console.error("Failed to send message:", error);
            throw error;
        }
    };

    const editMessage = async (conversationId: string, messageId: string, newText: string) => {
        if (!userProfile || !db) return;
        try {
            await db.collection('conversations').doc(conversationId).collection('messages').doc(messageId).update({
                text: newText,
                edited: true
            });
        } catch (error) {
            console.error("Failed to edit message:", error);
            throw error;
        }
    };

    const deleteMessage = async (conversationId: string, messageId: string) => {
        if (!userProfile || !db) return;
        try {
            await db.collection('conversations').doc(conversationId).collection('messages').doc(messageId).delete();
        } catch (error) {
            console.error("Failed to delete message:", error);
            throw error;
        }
    };

    const deleteConversation = async (conversationId: string) => {
        if (!userProfile || !db) return;
        try {
            await db.collection('conversations').doc(conversationId).delete();
        } catch (error) {
            console.error("Failed to delete conversation:", error);
            throw error;
        }
    };

    const reportMessage = async (conversationId: string, messageId: string, senderId: string, reason: string, details: string) => {
         if (!userProfile) return;
         try {
             const report: Omit<ReportedMessage, 'reportId'> = {
                 messageId,
                 conversationId,
                 senderId,
                 reason,
                 details,
                 reportedAt: TimestampValue.now()
             };
             await db.collection('reported_messages').add(report);
         } catch (error) {
             console.error("Failed to report message:", error);
             throw error;
         }
    };

    const getOrCreateConversation = useCallback(async (recipientId: string): Promise<string> => {
        if (!userProfile) return '';
        
        const participants = [userProfile.uid, recipientId].sort();
        const q = db.collection('conversations')
            .where('participants', '==', participants);

        const snapshot = await q.get();

        if (!snapshot.empty) {
            return snapshot.docs[0].id;
        }

        const newConvo = {
            participants: participants,
            lastMessage: null,
        };
        
        const docRef = await db.collection('conversations').add(newConvo);
        return docRef.id;

    }, [userProfile]);
    
    const markAsRead = (conversationId: string) => {
        if (!userProfile) return;
        const conversationMsgs = messages[conversationId] || [];
        const unreadMsgs = conversationMsgs.filter(m => !m.read && m.senderId !== userProfile.uid);
        
        unreadMsgs.forEach(msg => {
             db.collection('conversations').doc(conversationId).collection('messages').doc(msg.id).update({ read: true });
        });
    };

    const addReactionToMessage = async (conversationId: string, messageId: string, emoji: string) => {
        if (!userProfile || !db) return;
        const messageRef = db.collection('conversations').doc(conversationId).collection('messages').doc(messageId);
        
        try {
            await db.runTransaction(async (transaction: any) => {
                const msgDoc = await transaction.get(messageRef);
                if (!msgDoc.exists) return;

                const msgData = msgDoc.data() as ChatMessage;
                const reactions: Reaction = msgData.reactions || {};
                const reactors = reactions[emoji] || [];

                if (reactors.includes(userProfile.uid)) {
                    // User is removing their reaction
                    reactions[emoji] = reactors.filter(uid => uid !== userProfile.uid);
                    if (reactions[emoji].length === 0) {
                        delete reactions[emoji];
                    }
                } else {
                    // User is adding a new reaction
                    reactions[emoji] = [...reactors, userProfile.uid];
                }
                transaction.update(messageRef, { reactions });
            });
        } catch (error) {
            console.error("Error adding reaction to message:", error);
        }
    };

    const value: ChatContextType = {
        conversations,
        messages,
        totalUnreadCount,
        sendMessage,
        editMessage,
        deleteMessage,
        deleteConversation,
        reportMessage,
        getOrCreateConversation,
        markAsRead,
        addReactionToMessage,
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
